import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Link,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import API_BASE from '../apiBase';
import { getStoredUser } from '../utils/userStorage';

const COOKIE_CONSENT_KEY = process.env.REACT_APP_COOKIE_CONSENT_KEY;
const COOKIE_CONSENT_VERSION = process.env.REACT_APP_COOKIE_CONSENT_VERSION;

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [consentPreferences, setConsentPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already given consent
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      setShowBanner(true);
    } else {
      try {
        const consent = JSON.parse(savedConsent);
        
        // Validate consent object structure
        if (!consent || typeof consent !== 'object') {
          throw new Error('Invalid consent structure');
        }
        
        // Check if consent version matches (allows for policy updates)
        if (consent.version === COOKIE_CONSENT_VERSION) {
          // Validate and sanitize consent values
          setConsentPreferences({
            essential: true,
            analytics: Boolean(consent.analytics),
            marketing: Boolean(consent.marketing)
          });
        } else {
          // Policy updated, show banner again
          setShowBanner(true);
        }
      } catch (e) {
        // Invalid consent data, clear it and show banner
        try {
          localStorage.removeItem(COOKIE_CONSENT_KEY);
        } catch (clearError) {
          // Ignore clear errors
        }
        setShowBanner(true);
      }
    }
  }, []);

  // Sync consent to backend when user logs in
  useEffect(() => {
    const handleUserUpdate = () => {
      const user = getStoredUser();
      const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
      
      if (user && user.id && savedConsent) {
        try {
          const consent = JSON.parse(savedConsent);
          
          // Validate consent object structure
          if (!consent || typeof consent !== 'object') {
            return;
          }
          
          // Validate user.id format (MongoDB ObjectID is 24 hex characters)
          const userIdPattern = /^[0-9a-fA-F]{24}$/;
          if (!userIdPattern.test(user.id)) {
            console.error('Invalid user ID format');
            return;
          }
          
          // Sync consent to backend
          const syncConsent = async () => {
            try {
              await axios.put(`${API_BASE}/api/users/${user.id}/consent`, {
                cookie_consent: true,
                analytics_consent: Boolean(consent.analytics),
                marketing_consent: Boolean(consent.marketing)
              });
            } catch (error) {
              // Log error without exposing sensitive details
              console.error('Failed to sync consent to backend');
              // Log full error only in development
              if (process.env.NODE_ENV === 'development') {
                console.error(error);
              }
              // Don't block user if backend sync fails
            }
          };
          
          syncConsent();
        } catch (e) {
          // Invalid consent data, ignore
          console.error('Failed to parse consent for sync:', e);
        }
      }
    };
    
    // Listen for user updates (login/logout)
    window.addEventListener('userUpdated', handleUserUpdate);
    
    // Also check immediately in case user is already logged in
    handleUserUpdate();
    
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  const saveConsent = async (preferences) => {
    // Validate input
    if (!preferences || typeof preferences !== 'object') {
      console.error('Invalid preferences object');
      return;
    }

    // Ensure boolean values and validate
    const analytics = Boolean(preferences.analytics);
    const marketing = Boolean(preferences.marketing);

    const consentData = {
      version: COOKIE_CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      essential: true, // Always true
      analytics: analytics,
      marketing: marketing
    };

    // Save to localStorage
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    } catch (error) {
      console.error('Failed to save consent to localStorage:', error);
      // Continue even if localStorage fails
    }

    // Save to backend if user is logged in
    const user = getStoredUser();
    if (user && user.id) {
      // Validate user.id format (MongoDB ObjectID is 24 hex characters)
      const userIdPattern = /^[0-9a-fA-F]{24}$/;
      if (!userIdPattern.test(user.id)) {
        console.error('Invalid user ID format');
        return;
      }

      try {
        // Don't send timestamp - let backend set it server-side
        await axios.put(`${API_BASE}/api/users/${user.id}/consent`, {
          cookie_consent: true,
          analytics_consent: analytics,
          marketing_consent: marketing
        });
      } catch (error) {
        // Log error without exposing sensitive details
        console.error('Failed to save consent to backend');
        // Log full error only in development
        if (process.env.NODE_ENV === 'development') {
          console.error(error);
        }
        // Don't block user if backend save fails
      }
    }

    setConsentPreferences({
      essential: true,
      analytics: preferences.analytics || false,
      marketing: preferences.marketing || false
    });
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true
    });
  };

  const handleRejectAll = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false
    });
  };

  const handleSavePreferences = () => {
    saveConsent(consentPreferences);
  };

  const handlePreferenceChange = (type) => (event) => {
    // Validate type - only allow analytics and marketing
    const allowedTypes = ['analytics', 'marketing'];
    if (!allowedTypes.includes(type) || type === 'essential') {
      // Essential cookies cannot be disabled
      return;
    }
    
    // Ensure event is valid
    if (!event || typeof event.target?.checked !== 'boolean') {
      return;
    }

    setConsentPreferences(prev => ({
      ...prev,
      [type]: event.target.checked
    }));
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          p: 3,
          bgcolor: '#fff',
          borderTop: '1px solid #e0e0e0',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1f1f1f' }}>
                Cookie Consent
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                By clicking "Accept All", you consent to our use of cookies. You can also customize your preferences 
                or reject non-essential cookies. For more information, please read our{' '}
                <Link component={RouterLink} to="/cookie-policy" sx={{ color: '#000', fontWeight: 500, textDecoration: 'underline' }}>
                  Cookie Policy
                </Link>{' '}
                and{' '}
                <Link component={RouterLink} to="/privacy-policy" sx={{ color: '#000', fontWeight: 500, textDecoration: 'underline' }}>
                  Privacy Policy
                </Link>.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="outlined"
                onClick={() => setShowPreferences(true)}
                sx={{
                  borderColor: '#000',
                  color: '#000',
                  textTransform: 'none',
                  px: 3,
                  '&:hover': {
                    borderColor: '#333',
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Customize Preferences
              </Button>
              <Button
                variant="outlined"
                onClick={handleRejectAll}
                sx={{
                  borderColor: '#ccc',
                  color: '#666',
                  textTransform: 'none',
                  px: 3,
                  '&:hover': {
                    borderColor: '#999',
                    bgcolor: '#fafafa'
                  }
                }}
              >
                Reject All
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                onClick={handleAcceptAll}
                sx={{
                  bgcolor: '#000',
                  color: '#fff',
                  textTransform: 'none',
                  px: 4,
                  '&:hover': {
                    bgcolor: '#333'
                  }
                }}
              >
                Accept All
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {/* Preferences Dialog */}
      <Dialog
        open={showPreferences}
        onClose={() => setShowPreferences(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Cookie Preferences
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentPreferences.essential}
                    disabled
                    sx={{ '&.Mui-checked': { color: '#000' } }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Essential Cookies
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Required for the website to function properly. These cannot be disabled.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentPreferences.analytics}
                    onChange={handlePreferenceChange('analytics')}
                    sx={{ '&.Mui-checked': { color: '#000' } }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Analytics Cookies
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentPreferences.marketing}
                    onChange={handlePreferenceChange('marketing')}
                    sx={{ '&.Mui-checked': { color: '#000' } }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Marketing Cookies
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Used to track visitors across websites to display relevant advertisements and measure campaign effectiveness.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Typography variant="body2" sx={{ color: '#666', mt: 2 }}>
              You can change these preferences at any time. For more details, please read our{' '}
              <Link component={RouterLink} to="/cookie-policy" sx={{ color: '#000', fontWeight: 500 }}>
                Cookie Policy
              </Link>.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setShowPreferences(false)}
            sx={{ color: '#666', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePreferences}
            variant="contained"
            sx={{
              bgcolor: '#000',
              color: '#fff',
              textTransform: 'none',
              px: 3,
              '&:hover': {
                bgcolor: '#333'
              }
            }}
          >
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieBanner;

